import { useMemo, useState } from 'react';
import { useStudentData } from '@/hooks/useStudentData';
import WeekCalendar, {
  type LessonRow,
  type GroupInfo,
} from '@/pages/panel/program/components/WeekCalendar';
import LessonList from '@/pages/panel/program/components/LessonList';
import StudentLessonDetailModal from './components/LessonDetailModal';

type ViewMode = 'hafta' | 'liste';

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getWeekDates(anchor: Date): Date[] {
  const day = anchor.getDay();
  const diffToMonday = (day + 6) % 7;
  const monday = new Date(anchor);
  monday.setDate(anchor.getDate() - diffToMonday);
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    days.push(d);
  }
  return days;
}

function weekRangeLabel(days: Date[]): string {
  const first = days[0];
  const last = days[6];
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' };
  const f = first.toLocaleDateString('tr-TR', opts);
  const l = last.toLocaleDateString('tr-TR', { ...opts, year: 'numeric' });
  return `${f} - ${l}`;
}

export default function StudentProgram() {
  const { group, lessons, loading, error, refetch } = useStudentData();
  const [viewMode, setViewMode] = useState<ViewMode>('hafta');
  const [weekAnchor, setWeekAnchor] = useState<Date>(() => new Date());
  const [selectedLesson, setSelectedLesson] = useState<LessonRow | null>(null);

  const todayYmd = toYmd(new Date());

  const groupMap = useMemo<Record<string, GroupInfo>>(() => {
    if (!group) return {};
    return { [group.id]: { name: group.name, color: group.color ?? '#94a3b8' } };
  }, [group]);

  const lessonsByDate = useMemo(() => {
    const map: Record<string, LessonRow[]> = {};
    lessons.forEach((l) => {
      if (!l.date) return;
      if (!map[l.date]) map[l.date] = [];
      map[l.date].push(l as LessonRow);
    });
    return map;
  }, [lessons]);

  const weekDays = useMemo(() => getWeekDates(weekAnchor), [weekAnchor]);

  function shiftWeek(delta: number) {
    setWeekAnchor((prev) => {
      const d = new Date(prev);
      d.setDate(prev.getDate() + delta * 7);
      return d;
    });
  }

  function goToday() {
    setWeekAnchor(new Date());
  }

  if (loading) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
        <p className="mt-4 text-sm text-foreground-500">Ders programın yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl py-10">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
          <p className="mt-2 text-sm font-medium text-red-700">Ders programı yüklenirken bir hata oluştu.</p>
          <p className="mt-1 text-xs text-red-500">{error}</p>
          <button
            type="button"
            onClick={refetch}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-refresh-line" /> Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Ders Programım</h1>
        <p className="mt-1 text-sm text-foreground-600">
          Planlanan derslerini takvimde görüntüle ve canlı derslere katıl.
        </p>
      </div>

      {/* Görünüm kontrolü */}
      <div className="flex items-center gap-2 rounded-full bg-background-100 p-1">
        <button
          type="button"
          onClick={() => setViewMode('hafta')}
          className={`flex-1 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition cursor-pointer ${
            viewMode === 'hafta' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
          }`}
        >
          <i className="ri-calendar-2-line mr-1.5" /> Hafta
        </button>
        <button
          type="button"
          onClick={() => setViewMode('liste')}
          className={`flex-1 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition cursor-pointer ${
            viewMode === 'liste' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
          }`}
        >
          <i className="ri-list-check-3 mr-1.5" /> Liste
        </button>
      </div>

      {viewMode === 'hafta' && (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => shiftWeek(-1)}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-background-300 bg-white text-foreground-600 transition hover:bg-background-100 cursor-pointer"
                aria-label="Önceki hafta"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              <button
                type="button"
                onClick={goToday}
                className="whitespace-nowrap rounded-lg border border-background-300 bg-white px-3.5 py-2 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer"
              >
                Bugün
              </button>
              <button
                type="button"
                onClick={() => shiftWeek(1)}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-background-300 bg-white text-foreground-600 transition hover:bg-background-100 cursor-pointer"
                aria-label="Sonraki hafta"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </div>
            <p className="font-heading text-base font-semibold text-foreground-900">{weekRangeLabel(weekDays)}</p>
          </div>

          <WeekCalendar
            weekDays={weekDays}
            lessonsByDate={lessonsByDate}
            groupMap={groupMap}
            todayYmd={todayYmd}
            onSelectLesson={setSelectedLesson}
          />
        </>
      )}

      {viewMode === 'liste' && (
        <LessonList lessons={lessons as LessonRow[]} groupMap={groupMap} onSelectLesson={setSelectedLesson} />
      )}

      {viewMode === 'hafta' && lessons.length === 0 && (
        <div className="rounded-xl border border-dashed border-background-300 bg-white p-10 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-calendar-2-line text-2xl" />
          </div>
          <p className="mt-4 text-sm font-semibold text-foreground-800">
            Henüz size atanmış bir ders programı bulunmuyor.
          </p>
          <p className="mt-1 text-sm text-foreground-500">Öğretmeniniz ders planladığında burada görünecek.</p>
        </div>
      )}

      <StudentLessonDetailModal
        open={selectedLesson !== null}
        onClose={() => setSelectedLesson(null)}
        lesson={selectedLesson}
        group={group}
      />
    </div>
  );
}